import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { InternalTimeEnvelope } from "@/lib/profitability-api";

async function load(range?: string, personId?: string): Promise<InternalTimeEnvelope> {
  const params = new URLSearchParams();
  if (range) params.set("range", range);
  if (personId) params.set("person_id", personId);
  const qs = params.toString();
  const response = await fetch(`/api/internal-time${qs ? `?${qs}` : ""}`);
  if (!response.ok) {
    throw await engineError(response, "Could not load internal time");
  }
  return response.json();
}

/**
 * Internal and non-billable hours for every person who booked time.
 *
 * A hundred or so rows and no paging: the board is a whole-company denominator
 * -- the point of it is that the shares add up to one company -- and a page of
 * it would be a list of people rather than that. The columns sort in the
 * browser, the same arrangement the technology and Client Partner boards use.
 */
export function useInternalTime(range?: string) {
  return useQuery({
    queryKey: ["internal-time", range ?? "default"],
    queryFn: () => load(range),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * One person's hours opened out by where they went.
 *
 * Its own request rather than a field on the board: the detail is twenty-odd
 * lines per person across a hundred people, and nobody visiting the board
 * should pay for ten thousand rows to read one. `enabled` keeps it from firing
 * until a row is actually focused.
 */
export function useInternalTimeDetail(personId: string | null, range?: string) {
  return useQuery({
    queryKey: ["internal-time", range ?? "default", "detail", personId ?? "none"],
    queryFn: () => load(range, personId ?? undefined),
    enabled: personId !== null,
    staleTime: 5 * 60 * 1000,
  });
}
